import { AbsoluteFill, useCurrentFrame, useVideoConfig } from "remotion";
import { getEndCtaTiming } from "./endCtaTiming";

export const END_CTA_COPY = {
  brand: "LOOP NIBBLE",
  like: "LIKE",
  subscribe: "SUBSCRIBE",
} as const;

export type EndCtaVariant = "glass" | "minimal";

export type EndCtaProps = {
  variant?: EndCtaVariant;
  accentColor?: string;
  likeColor?: string;
};

/** Transparent overlay; intended for a Sequence over the existing final shot. */
export const EndCta = ({
  variant = "glass",
  accentColor = "#38CDBD",
  likeColor = "#FF4FA3",
}: EndCtaProps) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const timing = getEndCtaTiming(frame, fps);
  const s = Math.min(width / 1080, height / 1920);
  const isMinimal = variant === "minimal";

  return (
    <AbsoluteFill style={{ pointerEvents: "none", fontFamily: "Arial, sans-serif" }}>
      <div
        style={{
          position: "absolute",
          left: (width - 1080 * s) / 2 + 84 * s,
          bottom: 395 * s,
          width: 790 * s,
          boxSizing: "border-box",
          padding: isMinimal ? 18 * s : 25 * s,
          borderRadius: 31 * s,
          color: "#F4F8FF",
          background: isMinimal
            ? "rgba(8, 13, 24, 0.72)"
            : "linear-gradient(120deg, rgba(22, 36, 56, 0.94), rgba(8, 13, 24, 0.95))",
          border: isMinimal
            ? "1px solid rgba(255,255,255,0.13)"
            : "1px solid rgba(111,234,242,0.28)",
          boxShadow: isMinimal
            ? "0 7px 24px rgba(0,0,0,0.29)"
            : "0 18px 50px rgba(0,0,0,0.42)",
          opacity: timing.opacity,
          transform: "translateY(" + timing.translateY * s + "px) scale(" + timing.scale + ")",
          transformOrigin: "center bottom",
        }}
      >
        <div
          style={{
            fontSize: 20 * s,
            letterSpacing: 3.5 * s,
            fontWeight: 700,
            color: "#B4C4D5",
            marginBottom: 16 * s,
          }}
        >
          {END_CTA_COPY.brand}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 17 * s }}>
          <div
            style={{
              width: 65 * s,
              height: 65 * s,
              borderRadius: "50%",
              backgroundColor: "rgba(255,79,163,0.15)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <svg width={42 * s} height={42 * s} viewBox="0 0 24 24" aria-hidden="true">
              <g transform={`translate(12 ${20 + timing.likeLift}) rotate(${timing.likeRotate}) scale(${timing.likeScale}) translate(-12 -20)`}>
                <path d="M4 10 H7 V21 H4 Z M7 10 L11 9 L14 3 Q15 1 17 2 Q18 3 17 6 L16 9 H20 Q22 9 22 11 L20 19 Q20 21 18 21 H7 Z"
                  fill="#8294A8" stroke="#DCE9F7" strokeWidth=".65" />
                <path d="M4 10 H7 V21 H4 Z M7 10 L11 9 L14 3 Q15 1 17 2 Q18 3 17 6 L16 9 H20 Q22 9 22 11 L20 19 Q20 21 18 21 H7 Z"
                  fill={likeColor} opacity={timing.likeColorMix} />
              </g>
            </svg>
          </div>
          <div
            style={{
              fontWeight: 800,
              fontSize: 38 * s,
              letterSpacing: 1 * s,
              whiteSpace: "nowrap",
              marginRight: "auto",
            }}
          >
            {END_CTA_COPY.like}
          </div>
          <div
            style={{
              flexShrink: 0,
              padding: 17 * s,
              width: 376 * s,
              boxSizing: "border-box",
              borderRadius: 17 * s,
              textAlign: "center",
              fontWeight: 800,
              fontSize: 33 * s,
              letterSpacing: 0.75 * s,
              backgroundColor: accentColor,
              color: "#071522",
              transform: "scale(" + (1 + timing.pulse * 0.035) + ")",
              boxShadow: "0 0 " + (12 + timing.pulse * 18) * s + "px rgba(56,205,189,0.22)",
            }}
          >
            {END_CTA_COPY.subscribe}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
